import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, switchMap, throwError } from "rxjs";
import { AuthService } from "../services/auth.service";

/**
 * Rotas de sessão não entram no caminho de renovação: um 401 de senha errada
 * viraria uma tentativa de refresh, e o erro certo nunca chegaria à tela.
 */
const SESSION_ROUTES = ["/auth/login", "/auth/refresh", "/auth/recuperar-senha"];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  const prepare = () => {
    const token = authService.getToken();
    return req.clone({
      /*
       * Sem isto o navegador **não anexa o cookie de sessão** (spec 021), nem
       * para subdomínio irmão — e o `/auth/refresh` falharia com a sessão
       * perfeitamente válida do outro lado. Vai em todas as requisições; quem
       * limita o alcance do cookie é o `Path=/auth` com que ele foi gravado.
       */
      withCredentials: true,
      ...(token ? { setHeaders: { Authorization: `Bearer ${token}` } } : {}),
    });
  };

  return next(prepare()).pipe(
    catchError((error: HttpErrorResponse) => {
      const isSessionRoute = SESSION_ROUTES.some((route) =>
        req.url.includes(route),
      );

      if (error.status !== 401 || isSessionRoute) {
        return throwError(() => error);
      }

      /*
       * O ID Token do Firebase dura uma hora, então tomar 401 no meio do uso é
       * o caso comum, não a exceção. Renova **uma vez** e repete a requisição;
       * só desloga se a renovação também falhar.
       *
       * O gatilho é `mayHaveSession()` e não mais `hasRefreshToken()`: o
       * refresh token virou `httpOnly` e este código não o enxerga. Sem esse
       * porém, um 401 qualquer mandaria o visitante anônimo para o login com
       * um aviso de "sessão expirada" que nunca existiu.
       */
      if (authService.mayHaveSession()) {
        return authService.refresh().pipe(
          switchMap(() => next(prepare())),
          catchError(() => {
            authService.logout(true);
            return throwError(() => error);
          }),
        );
      }

      if (authService.isLoggedIn()) {
        authService.logout(true);
      }
      return throwError(() => error);
    }),
  );
};
